import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
  Button,
  Input,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import classnames from "classnames";
import api from "../../api";
import Breadcrumbs from "../../components/Common/Breadcrumb";
// PopupModal replaced with Reactstrap Alert & Modal
const SabrinaBuy = () => {
  document.title = "Pokémon Radiance | Sell to Sabrina";

  // Tabs: "shiny" or "normal"
  const [activeTab, setActiveTab] = useState("shiny");

  // Data arrays for Shiny vs. Normal
  const [shinyList, setShinyList] = useState([]);
  const [normalList, setNormalList] = useState([]);

  // Loading / error states
  const [loadingShiny, setLoadingShiny] = useState(true);
  const [loadingNormal, setLoadingNormal] = useState(true);
  const [, setError] = useState("");

  // Searching and filtering
  const [searchShiny, setSearchShiny] = useState("");
  const [searchNormal, setSearchNormal] = useState("");
  const [typeFilterShiny, setTypeFilterShiny] = useState("");
  const [typeFilterNormal, setTypeFilterNormal] = useState("");

  // Selected Pokémon IDs for each tab
  const [selectedShiny, setSelectedShiny] = useState(new Set());
  const [selectedNormal, setSelectedNormal] = useState(new Set());

  // Popup for confirmation / error messages
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupColor, setPopupColor] = useState("success");

  // confirmation modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmType, setConfirmType] = useState("sell"); // "sell" or "nfs"
  const confirmActionRef = useRef(null);

  const openConfirm = (message, action, type = "sell") => {
    setConfirmMessage(message);
    setConfirmType(type);
    confirmActionRef.current = action;
    setConfirmOpen(true);
  };

  const handleConfirmYes = () => {
    if (confirmActionRef.current) confirmActionRef.current();
    setConfirmOpen(false);
  };

  // NFS toggle functionality
  const handleToggleNFS = async (pokeId, currentNFSStatus) => {
    try {
      const endpoint = currentNFSStatus ? "/v1/nfs/unmark" : "/v1/nfs/mark";
      const response = await api.post(endpoint, null, {
        params: { poke_id: pokeId },
        withCredentials: true
      });
      
      if (response.data.success) {
        // Refresh the data from server instead of just updating local state
        if (activeTab === "shiny") {
          await fetchShiny(typeFilterShiny);
        } else {
          await fetchNormal(typeFilterNormal);
        }
        
        setPopupMessage(response.data.message);
        setPopupColor("success");
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      }
    } catch (err) {
      console.error("NFS toggle error:", err);
      setPopupMessage(err.response?.data?.detail || "Failed to update NFS status");
      setPopupColor("danger");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  // Pokemon types for filter dropdown
  const pokemonTypes = [
    "Normal", "Fire", "Water", "Electric", "Grass", "Ice", "Fighting", "Poison",
    "Ground", "Flying", "Psychic", "Bug", "Rock", "Ghost", "Dragon", "Dark", "Steel", "Fairy"
  ];

  // ========== FETCH DATA ==========

  const fetchShiny = async (typeFilter = "") => {
    setLoadingShiny(true);
    try {
      let url = "/v1/sabrinabuy/eligible?type=shiny";
      if (typeFilter) {
        url += `&pokemon_type=${encodeURIComponent(typeFilter)}`;
      }
      const response = await api.get(url, { withCredentials: true });
      setShinyList(response.data || []);
    } catch (err) {
      console.error("Failed to load shiny Pokémon", err);
      setError("Failed to load shiny Pokémon.");
    } finally {
      setLoadingShiny(false);
    }
  };

  const fetchNormal = async (typeFilter = "") => {
    setLoadingNormal(true);
    try {
      let url = "/v1/sabrinabuy/eligible?type=normal";
      if (typeFilter) {
        url += `&pokemon_type=${encodeURIComponent(typeFilter)}`;
      }
      const response = await api.get(url, { withCredentials: true });
      setNormalList(response.data || []);
    } catch (err) {
      console.error("Failed to load normal Pokémon", err);
      setError("Failed to load normal Pokémon.");
    } finally {
      setLoadingNormal(false);
    }
  };

  useEffect(() => {
    fetchShiny();
    fetchNormal();
  }, []);

  // Handle type filter changes
  const handleShinyTypeFilter = (type) => {
    setTypeFilterShiny(type);
    setSelectedShiny(new Set()); // Clear selections when filter changes
    fetchShiny(type);
  };

  const handleNormalTypeFilter = (type) => {
    setTypeFilterNormal(type);
    setSelectedNormal(new Set()); // Clear selections when filter changes
    fetchNormal(type);
  };

  // ========== TAB HANDLING ==========
  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  // ========== SEARCH FILTERS ==========

  const filteredShiny = useMemo(
    () =>
      shinyList.filter((mon) =>
        mon.name.toLowerCase().includes(searchShiny.toLowerCase())
      ),
    [shinyList, searchShiny]
  );

  const filteredNormal = useMemo(
    () =>
      normalList.filter((mon) =>
        mon.name.toLowerCase().includes(searchNormal.toLowerCase())
      ),
    [normalList, searchNormal]
  );

  // ========== SELECT / DESELECT LOGIC ==========

  // Shiny
  const handleShinySelect = (pokeId) => {
    // Check if Pokemon is NFS
    const pokemon = shinyList.find(mon => mon.id === pokeId);
    if (pokemon?.is_nfs) return; // Don't allow selection of NFS Pokemon
    
    setSelectedShiny((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pokeId)) {
        newSet.delete(pokeId);
      } else {
        newSet.add(pokeId);
      }
      return newSet;
    });
  };
  const handleShinySelectAll = () => {
    // Only select non-NFS Pokemon
    const selectableShiny = filteredShiny.filter(mon => !mon.is_nfs);
    
    if (selectedShiny.size === selectableShiny.length) {
      // unselect all
      setSelectedShiny(new Set());
    } else {
      // select all selectable
      const allIds = new Set(selectableShiny.map((mon) => mon.id));
      setSelectedShiny(allIds);
    }
  };

  // Normal
  const handleNormalSelect = (pokeId) => {
    // Check if Pokemon is NFS
    const pokemon = normalList.find(mon => mon.id === pokeId);
    if (pokemon?.is_nfs) return; // Don't allow selection of NFS Pokemon
    
    setSelectedNormal((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pokeId)) {
        newSet.delete(pokeId);
      } else {
        newSet.add(pokeId);
      }
      return newSet;
    });
  };
  const handleNormalSelectAll = () => {
    // Only select non-NFS Pokemon
    const selectableNormal = filteredNormal.filter(mon => !mon.is_nfs);
    
    if (selectedNormal.size === selectableNormal.length) {
      setSelectedNormal(new Set());
    } else {
      const allIds = new Set(selectableNormal.map((mon) => mon.id));
      setSelectedNormal(allIds);
    }
  };

  // ========== SELL POKEMON ==========

  const handleSellShiny = async (overrideIds = null) => {
    const idsToSell = overrideIds || Array.from(selectedShiny);
    if (!idsToSell.length) {
      setPopupMessage("No shiny Pokémon selected to sell.");
      setPopupColor("danger");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }
    try {
      const response = await api.post(
        "/v1/sabrinabuy",
        { poke_ids: idsToSell },
        { withCredentials: true }
      );
      if (response.data.success) {
        setPopupMessage(response.data.message);
        setPopupColor("success");
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 4000);
        // refresh list locally
        setShinyList((prev) =>
          prev.filter((mon) => !idsToSell.includes(mon.id))
        );
        setSelectedShiny(new Set());
      } else {
        setPopupMessage(response.data.detail || "Sell failed.");
        setPopupColor("danger");
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      }
    } catch (err) {
      console.error("Sell shiny error:", err);
      let msg = "Sell shiny Pokémon failed.";
      if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      setPopupMessage(msg);
      setPopupColor("danger");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  const handleSellNormal = async (overrideIds = null) => {
    const idsToSell = overrideIds || Array.from(selectedNormal);
    if (!idsToSell.length) {
      setPopupMessage("No normal Pokémon selected to sell.");
      setPopupColor("danger");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }
    try {
      const response = await api.post(
        "/v1/sabrinabuy",
        { poke_ids: idsToSell },
        { withCredentials: true }
      );
      if (response.data.success) {
        setPopupMessage(response.data.message);
        setPopupColor("success");
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 4000);
        // refresh list locally
        setNormalList((prev) =>
          prev.filter((mon) => !idsToSell.includes(mon.id))
        );
        setSelectedNormal(new Set());
      } else {
        setPopupMessage(response.data.detail || "Sell failed.");
        setPopupColor("danger");
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      }
    } catch (err) {
      console.error("Sell normal error:", err);
      let msg = "Sell normal Pokémon failed.";
      if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      setPopupMessage(msg);
      setPopupColor("danger");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Sabrina" breadcrumbItem="Sell Pokémon" />

          <Row>
            <Col md={12}>
              {/* TABS */}
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "shiny" })}
                    onClick={() => toggleTab("shiny")}
                    style={{ cursor: "pointer" }}
                  >
                    Shiny Pokémon
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "normal" })}
                    onClick={() => toggleTab("normal")}
                    style={{ cursor: "pointer" }}
                  >
                    Normal Pokémon
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab} className="p-3">
                {/* SHINY TAB */}
                <TabPane tabId="shiny">
                  {/* Sabrina’s header & controls */}
                  <Card
                    style={{ background: "#1d222e", border: "none" }}
                    className="mb-4"
                  >
                    <CardBody>
                      <Row className="align-items-center mb-3">
                        <Col md="2" className="text-center">
                          <img
                            src="/avatars/20.png"
                            alt="Sabrina"
                            style={{ width: 80, borderRadius: "50%" }}
                          />
                        </Col>
                        <Col md="10">
                          <div style={{ color: "#ccc" }}>
                            <ul
                              style={{
                                listStyle: "none",
                                padding: 0,
                                margin: 0,
                              }}
                            >
                              <li
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: 4,
                                }}
                              >
                                <img
                                  src="/items/coin.png"
                                  alt="coin"
                                  style={{ width: 16, marginRight: 8 }}
                                />
                                <span>
                                  20–50 <strong>coins</strong> per Pokémon
                                </span>
                              </li>
                              <li
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/items/money.png"
                                  alt="money"
                                  style={{ width: 16, marginRight: 8 }}
                                />
                                <span>
                                  5,000–8,000 <strong>money</strong> per
                                  Pokémon
                                </span>
                              </li>
                            </ul>
                            <p
                              style={{
                                fontSize: "0.85rem",
                                color: "#aaa",
                                marginTop: "0.75rem",
                              }}
                            >
                              Bulk Bonus: +10% at 20+, +20% at 100+, +30% at
                              500+
                            </p>
                            {/* <div
                              style={{
                                fontSize: "0.85rem",
                                color: "#ffd700",
                                marginTop: "0.5rem",
                                background: "rgba(255, 215, 0, 0.1)",
                                border: "1px solid #ffd700",
                                padding: "5px",
                                borderRadius: "4px",
                              }}
                            >
                              <strong>NEW!</strong> Equip the Ring of Selling  to get +10% bonus coins and money when selling Pokémon! 
                            </div> */}
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md="3">
                          <Input
                            type="text"
                            placeholder="Search shiny..."
                            value={searchShiny}
                            onChange={(e) =>
                              setSearchShiny(e.target.value)
                            }
                            style={{
                              background: "#272d39",
                              border: "1px solid #444",
                              color: "#fff",
                            }}
                          />
                        </Col>
                        <Col md="3">
                          <Input
                            type="select"
                            value={typeFilterShiny}
                            onChange={(e) => handleShinyTypeFilter(e.target.value)}
                            style={{
                              background: "#272d39",
                              border: "1px solid #444",
                              color: "#fff",
                            }}
                          >
                            <option value="">All Types</option>
                            {pokemonTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </Input>
                        </Col>
                        <Col md="6" className="text-end">
                          <Button
                            color="info"
                            className="me-2"
                            onClick={handleShinySelectAll}
                          >
                            {selectedShiny.size === filteredShiny.filter(mon => !mon.is_nfs).length
                              ? "Unselect All"
                              : "Select All"}
                          </Button>
                          <Button
                            color="warning"
                            className="me-2"
                            onClick={() => {
                              if (selectedShiny.size === 0) {
                                setPopupMessage("No Pokémon selected to mark as NFS.");
                                setPopupColor("danger");
                                setShowPopup(true);
                                setTimeout(() => setShowPopup(false), 3000);
                                return;
                              }
                              openConfirm(
                                `Mark ${selectedShiny.size} selected Pokémon as NFS (Not For Sale)?`,
                                async () => {
                                  for (const pokeId of selectedShiny) {
                                    await handleToggleNFS(pokeId, false);
                                  }
                                  setSelectedShiny(new Set());
                                },
                                "nfs"
                              );
                            }}
                            disabled={!selectedShiny.size}
                          >
                            Mark as NFS
                          </Button>
                          <Button
                            color="success"
                            className="me-2"
                            onClick={() => handleSellShiny()}
                            disabled={!selectedShiny.size}
                          >
                            Sell Selected
                          </Button>
                          <Button
                            color="danger"
                            onClick={() =>
                              openConfirm(`Sell all ${filteredShiny.filter(mon => !mon.is_nfs).length} shiny Pokémon?`, () => handleSellShiny(filteredShiny.filter(mon => !mon.is_nfs).map((m) => m.id)))
                            }
                            disabled={!filteredShiny.filter(mon => !mon.is_nfs).length}
                          >
                            Sell All
                          </Button>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* Card grid */}
                  {loadingShiny ? (
                    <p style={{ color: "#fff" }}>Loading…</p>
                  ) : filteredShiny.length === 0 ? (
                    <p className="text-center text-muted">
                      No shiny Pokémon to sell.
                    </p>
                  ) : (
                    <Row className="g-3">
                      {filteredShiny.map((mon) => {
                        const isSelected = selectedShiny.has(mon.id);
                        return (
                          <Col key={mon.id} xs="12" sm="6" md="4" lg="3">
                            <Card
                              className="pokedex-card"
                              style={{
                                background: "#272d39",
                                border: isSelected
                                  ? "2px solid #4caf50"
                                  : mon.is_nfs ? "2px solid #ccc" : "none",
                                borderRadius: "8px",
                                color: "#fff",
                                cursor: mon.is_nfs ? "not-allowed" : "pointer",
                              }}
                              onClick={() => !mon.is_nfs && handleShinySelect(mon.id)}
                            >
                              <CardBody className="position-relative text-center">
                                {/* checkbox */}
                                <label
                                  style={{
                                    position: "absolute",
                                    top: 8,
                                    left: 8,
                                    zIndex: 10,
                                    cursor: "pointer",  // so user gets pointer cursor
                                  }}
                                  onClick={(e) => e.stopPropagation()}  // prevent Card click
                                >
                                  <Input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleShinySelect(mon.id)}
                                    style={{
                                      pointerEvents: "auto", 
                                    }}
                                    disabled={mon.is_nfs}
                                  />
                                </label>

                                {/* sprite */}
                                <img
                                  src={`/sprites/${mon.name}.png`}
                                  alt={mon.name}
                                  style={{
                                    width: 128,
                                    height: 128,
                                    marginBottom: 8,
                                  }}
                                />

                                {/* name & level */}
                                <h5
                                  style={{
                                    margin: "0.5rem 0",
                                    fontWeight: 600,
                                  }}
                                >
                                  {mon.name}
                                </h5>
                                <p
                                  style={{ color: "#ccc", margin: 0 }}
                                >
                                  Lv. {mon.level}
                                </p>
                                {mon.is_nfs === 1 && (
                                  <Button
                                    color="warning"
                                    size="sm"
                                    className="mt-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleNFS(mon.id, true);
                                    }}
                                  >
                                    Unmark NFS
                                  </Button>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </TabPane>

                {/* NORMAL TAB */}
                <TabPane tabId="normal">
                  {/* Sabrina’s header & controls */}
                  <Card
                    style={{ background: "#1d222e", border: "none" }}
                    className="mb-4"
                  >
                    <CardBody>
                      <Row className="align-items-center mb-3">
                        <Col md="2" className="text-center">
                          <img
                            src="/avatars/20.png"
                            alt="Sabrina"
                            style={{ width: 80, borderRadius: "50%" }}
                          />
                        </Col>
                        <Col md="10">
                          <div style={{ color: "#ccc" }}>
                            <p
                              style={{
                                fontSize: "0.95rem",
                                marginBottom: "0.5rem",
                              }}
                            >
                              Sabrina will buy{" "}
                              <strong>Normal</strong> Pokémon for:
                            </p>
                            <ul
                              style={{
                                listStyle: "none",
                                padding: 0,
                                margin: 0,
                              }}
                            >
                              <li
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: 4,
                                }}
                              >
                                <img
                                  src="/items/coin.png"
                                  alt="coin"
                                  style={{ width: 16, marginRight: 8 }}
                                />
                                <span>
                                  1-5 <strong>coins</strong> per Pokémon
                                </span>
                              </li>
                              <li
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/items/money.png"
                                  alt="money"
                                  style={{ width: 16, marginRight: 8 }}
                                />
                                <span>
                                  500–2,000 <strong>money</strong> per
                                  Pokémon
                                </span>
                              </li>
                            </ul>
                            <p
                              style={{
                                fontSize: "0.85rem",
                                color: "#aaa",
                                marginTop: "0.75rem",
                              }}
                            >
                              Bulk Bonus: +10% at 20+, +20% at 100+, +30% at
                              500+
                            </p>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md="3">
                          <Input
                            type="text"
                            placeholder="Search normal..."
                            value={searchNormal}
                            onChange={(e) =>
                              setSearchNormal(e.target.value)
                            }
                            style={{
                              background: "#272d39",
                              border: "1px solid #444",
                              color: "#fff",
                            }}
                          />
                        </Col>
                        <Col md="3">
                          <Input
                            type="select"
                            value={typeFilterNormal}
                            onChange={(e) => handleNormalTypeFilter(e.target.value)}
                            style={{
                              background: "#272d39",
                              border: "1px solid #444",
                              color: "#fff",
                            }}
                          >
                            <option value="">All Types</option>
                            {pokemonTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </Input>
                        </Col>
                        <Col md="6" className="text-end">
                          <Button
                            color="info"
                            className="me-2"
                            onClick={handleNormalSelectAll}
                          >
                            {selectedNormal.size === filteredNormal.length
                              ? "Unselect All"
                              : "Select All"}
                          </Button>
                          <Button
                            color="warning"
                            className="me-2"
                            onClick={() => {
                              if (selectedNormal.size === 0) {
                                setPopupMessage("No Pokémon selected to mark as NFS.");
                                setPopupColor("danger");
                                setShowPopup(true);
                                setTimeout(() => setShowPopup(false), 3000);
                                return;
                              }
                              openConfirm(
                                `Mark ${selectedNormal.size} selected Pokémon as NFS (Not For Sale)?`,
                                async () => {
                                  for (const pokeId of selectedNormal) {
                                    await handleToggleNFS(pokeId, false);
                                  }
                                  setSelectedNormal(new Set());
                                },
                                "nfs"
                              );
                            }}
                            disabled={!selectedNormal.size}
                          >
                            Mark as NFS
                          </Button>
                          <Button
                            color="success"
                            onClick={() => handleSellNormal()}
                            disabled={!selectedNormal.size}
                          >
                            Sell Selected
                          </Button>
                          <Button
                            color="danger"
                            className="ms-2"
                            onClick={() =>
                              openConfirm(`Sell all ${filteredNormal.filter(mon => !mon.is_nfs).length} normal Pokémon?`, () => handleSellNormal(filteredNormal.filter(mon => !mon.is_nfs).map((m) => m.id)))
                            }
                            disabled={!filteredNormal.filter(mon => !mon.is_nfs).length}
                          >
                            Sell All
                          </Button>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* Card grid */}
                  {loadingNormal ? (
                    <p style={{ color: "#fff" }}>Loading…</p>
                  ) : filteredNormal.length === 0 ? (
                    <p className="text-center text-muted">
                      No normal Pokémon to sell.
                    </p>
                  ) : (
                    <Row className="g-3">
                      {filteredNormal.map((mon) => {
                        const isSelected = selectedNormal.has(mon.id);
                        return (
                          <Col key={mon.id} xs="12" sm="6" md="4" lg="3">
                            <Card
                              className="pokedex-card"
                              style={{
                                background: "#272d39",
                                border: isSelected
                                  ? "2px solid #4caf50"
                                  : mon.is_nfs ? "2px solid #ccc" : "none",
                                borderRadius: "8px",
                                color: "#fff",
                                cursor: mon.is_nfs ? "not-allowed" : "pointer",
                              }}
                              onClick={() => !mon.is_nfs && handleNormalSelect(mon.id)}
                            >
                              <CardBody className="position-relative text-center">
                                {/* checkbox */}
                                <label
                                  style={{
                                    position: "absolute",
                                    top: 8,
                                    left: 8,
                                    zIndex: 10,
                                    cursor: "pointer",  
                                  }}
                                  onClick={(e) => e.stopPropagation()}  
                                >
                                  <Input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleNormalSelect(mon.id)}
                                    style={{
                                      pointerEvents: "auto",  
                                    }}
                                    disabled={mon.is_nfs}
                                  />
                                </label>

                                {/* sprite */}
                                <img
                                  src={`/sprites/${mon.name}.png`}
                                  alt={mon.name}
                                  style={{
                                    width: 128,
                                    height: 128,
                                    marginBottom: 8,
                                  }}
                                />

                                {/* name & level */}
                                <h5
                                  style={{
                                    margin: "0.5rem 0",
                                    fontWeight: 600,
                                  }}
                                >
                                  {mon.name}
                                </h5>
                                <p
                                  style={{ color: "#ccc", margin: 0 }}
                                >
                                  Lv. {mon.level}
                                </p>
                                {mon.is_nfs === 1 && (
                                  <Button
                                    color="warning"
                                    size="sm"
                                    className="mt-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleNFS(mon.id, true);
                                    }}
                                  >
                                    Unmark NFS
                                  </Button>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        </Container>
      </div>

      {showPopup && (
        <Alert
          color={popupColor}
          className="position-fixed top-0 start-50 translate-middle-x mt-3 shadow"
          toggle={() => setShowPopup(false)}
          fade
          style={{ zIndex: 9999, minWidth: "300px" }}
        >
          {popupMessage}
        </Alert>
      )}

      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered>
        <ModalHeader toggle={() => setConfirmOpen(false)}>Confirm {confirmType === "sell" ? "Sell" : "NFS Marking"}</ModalHeader>
        <ModalBody>{confirmMessage}</ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleConfirmYes}>
            Yes, {confirmType === "sell" ? "Sell" : "Mark as NFS"}
          </Button>{" "}
          <Button color="secondary" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SabrinaBuy;
